import type { JsonSchemaNode } from "@nmblr-platform-flow/schema";
import { X } from "@phosphor-icons/react";
import { useCallback, useState } from "react";
import type { ComponentType } from "react";
import { humanize } from "../../../lib/humanize.js";
import type { TemplateVariable } from "../../../lib/schema-paths.js";

interface SchemaFieldProps {
	name: string;
	schema: JsonSchemaNode;
	value: unknown;
	onChange: (value: unknown) => void;
	required?: boolean;
	templateVariables?: TemplateVariable[];
}

interface Props {
	name: string;
	schema: JsonSchemaNode;
	value: unknown;
	onChange: (value: unknown) => void;
	required?: boolean;
	SchemaField: ComponentType<SchemaFieldProps>;
	templateVariables?: TemplateVariable[];
}

export function ArrayFields({
	name,
	schema,
	value,
	onChange,
	required,
	SchemaField,
	templateVariables,
}: Props) {
	const arr = Array.isArray(value) ? value : [];
	const itemSchema = schema.items;
	const hasTypedItems =
		itemSchema &&
		itemSchema.type === "object" &&
		itemSchema.properties &&
		Object.keys(itemSchema.properties).length > 0;

	const [jsonErrors, setJsonErrors] = useState<Record<number, string>>({});

	const updateItem = useCallback(
		(index: number, item: unknown) => {
			const updated = [...arr];
			updated[index] = item;
			onChange(updated);
		},
		[arr, onChange],
	);

	const addItem = useCallback(
		() => onChange([...arr, hasTypedItems ? {} : ""]),
		[arr, hasTypedItems, onChange],
	);
	const removeItem = useCallback(
		(index: number) => {
			onChange(arr.filter((_, i) => i !== index));
			setJsonErrors((errs) => {
				const next: Record<number, string> = {};
				for (const [k, v] of Object.entries(errs)) {
					const i = Number(k);
					if (i < index) next[i] = v;
					else if (i > index) next[i - 1] = v;
					// i === index: dropped
				}
				return next;
			});
		},
		[arr, onChange],
	);

	return (
		<div className="mb-3">
			<div className="mb-1 text-xs text-text-muted">
				{humanize(name)}
				{required && <span className="ml-1 text-error">*</span>}
			</div>
			{schema.description && <p className="mb-2 text-xs text-text-muted">{schema.description}</p>}
			{arr.map((item, index) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: array items have no stable identity
				<div key={index} className="mb-2 flex gap-2">
					<div className="flex-1">
						{hasTypedItems ? (
							<SchemaField
								name={`${name}[${index}]`}
								schema={itemSchema}
								value={item}
								onChange={(v) => updateItem(index, v)}
								templateVariables={templateVariables}
							/>
						) : (
							<div>
								<textarea
									value={typeof item === "string" ? item : JSON.stringify(item, null, 2)}
									onChange={(e) => {
										const raw = e.target.value;
										// Attempt JSON parse for object/array literals; treat everything else as a plain string
										if (raw.trimStart().startsWith("{") || raw.trimStart().startsWith("[")) {
											try {
												updateItem(index, JSON.parse(raw));
												setJsonErrors((errs) => {
													const next = { ...errs };
													delete next[index];
													return next;
												});
											} catch {
												setJsonErrors((errs) => ({ ...errs, [index]: "Invalid JSON" }));
											}
										} else {
											updateItem(index, raw);
											setJsonErrors((errs) => {
												const next = { ...errs };
												delete next[index];
												return next;
											});
										}
									}}
									rows={3}
									className="block w-full rounded-xl border border-border bg-canvas px-3 py-2 font-mono text-xs text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
								/>
								{jsonErrors[index] && (
									<span className="mt-1 text-xs text-error">{jsonErrors[index]}</span>
								)}
							</div>
						)}
					</div>
					<button
						type="button"
						aria-label="Remove item"
						onClick={() => removeItem(index)}
						className="self-start rounded-xl border border-error/30 px-2 py-1 text-xs text-error hover:bg-error/10"
					>
						<X size={14} />
					</button>
				</div>
			))}
			<button
				type="button"
				onClick={addItem}
				className="rounded-xl border border-border px-3 py-1 text-xs text-text-muted hover:bg-canvas"
			>
				+ Add item
			</button>
		</div>
	);
}
